import { selectMatchingVideoSource } from '../utils';

// Vanaf een kwart in beeld speelt de video, daaronder pauzeert hij weer
const PLAY_THRESHOLD = 0.25;
// De bron wordt een hele viewport voor het in beeld komen ingeladen, dan staat de
// eerste frame er meestal al voordat de video in beeld komt
const PRELOAD_MARGIN = '100%';

/**
 * Video die gedempt automatisch speelt zolang hij in beeld is en daarbuiten
 * pauzeert. Het loopen doet de browser zelf via het loop-attribuut.
 *
 * De video fade in zodra zijn eerste frame binnen is, met dezelfde overgang als
 * de lazy plaatjes, zodat een videotegel net zo binnenkomt als een plaatjestegel.
 * Het posterplaatje is puur achtervang: het komt alleen in beeld als de video
 * niets kan tonen.
 */
export default class AutoplayVideo {
	constructor(element) {
		this.element = element;
		this.video = element.querySelector('video');

		if (!this.video) {
			return;
		}

		this.poster = element.querySelector('[data-autoplay-poster]');

		this.prepared = false;
		this.ready = false;
		this.fallback = false;

		// Zowel het attribuut als de property, iOS start alleen automatisch als
		// de video echt gedempt is
		this.video.muted = true;

		this.onLoadedData = this.onLoadedData.bind(this);
		this.onError = this.onError.bind(this);
		this.onPlayIntersect = this.onPlayIntersect.bind(this);
		this.onPreloadIntersect = this.onPreloadIntersect.bind(this);

		this.video.addEventListener('loadeddata', this.onLoadedData);
		this.video.addEventListener('error', this.onError);

		this.playObserver = new IntersectionObserver(this.onPlayIntersect, { threshold: PLAY_THRESHOLD });
		this.playObserver.observe(this.element);

		this.preloadObserver = new IntersectionObserver(this.onPreloadIntersect, { rootMargin: PRELOAD_MARGIN });
		this.preloadObserver.observe(this.element);
	}

	/**
	 * De bron kiezen die past bij de weergavegrootte en laten inladen. Dit
	 * gebeurt één keer: bij een resize opnieuw kiezen zou de video midden in
	 * het afspelen herladen.
	 */
	prepare() {
		if (this.prepared || this.fallback) {
			return;
		}

		// Zonder afmetingen valt er niets te kiezen, dan zou de kleinste bron
		// gepakt worden. Later, bij het afspelen, staat de video er wel
		if (!this.element.getBoundingClientRect().width) {
			return;
		}

		// Wie minder beweging wil hoeft de video niet eens te downloaden, voor
		// hem is het plaatje het beeld
		if (this.prefersReducedMotion() && this.poster) {
			this.showFallback();
			return;
		}

		this.prepared = true;

		this.video.preload = 'auto';
		const source = selectMatchingVideoSource(this.video);

		// Bij een video die al ingeladen was is de eerste frame er misschien al
		if (this.video.readyState >= 2) {
			this.setReady();
		}
	}

	play() {
		if (this.fallback) {
			return;
		}

		this.prepare();

		// Zonder poster om op terug te vallen blijft de video op zijn eerste
		// frame staan, dat is de stille variant van hetzelfde beeld
		if (this.prefersReducedMotion()) {
			return;
		}

		const playing = this.video.play();

		if (playing && typeof playing.catch === 'function') {
			playing.catch((error) => {
				// Alleen een geweigerde autoplay is een reden voor de achtervang,
				// een afgebroken play() (snel doorscrollen) niet
				if (error && error.name === 'NotAllowedError') {
					this.showFallback();
				}
			});
		}
	}

	pause() {
		this.video.pause();
	}

	/**
	 * @returns {Boolean} of de bezoeker zo weinig beweging als mogelijk wil
	 */
	prefersReducedMotion() {
		if (typeof window.matchMedia !== 'function') {
			return false;
		}

		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	/**
	 * Er is beeld, de video mag in fade
	 */
	setReady() {
		if (this.ready) {
			return;
		}

		this.ready = true;
		this.element.classList.add('is-ready');
	}

	/**
	 * Het plaatje tonen in plaats van de video: bij een fout, bij een geweigerde
	 * autoplay of als de bezoeker minder beweging wil
	 */
	showFallback() {
		if (this.fallback || !this.poster) {
			return;
		}

		this.fallback = true;
		this.ready = false;

		this.video.pause();
		this.element.classList.remove('is-ready');
		this.element.classList.add('is-fallback');

		// Pas nu inladen, in het normale geval wordt het plaatje dus nooit
		// gedownload. De klasse staat er al, dus lazysizes kan de afmetingen
		// van het plaatje meten
		const image = this.poster.querySelector('img.lazyload');

		if (image) {
			window.lazySizes?.loader?.unveil(image);
		}
	}

	onLoadedData() {
		this.setReady();
	}

	onError() {
		this.showFallback();
	}

	/**
	 * @param {IntersectionObserverEntry[]} entries
	 */
	onPlayIntersect(entries) {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				this.play();
			} else {
				this.pause();
			}
		});
	}

	/**
	 * @param {IntersectionObserverEntry[]} entries
	 */
	onPreloadIntersect(entries) {
		if (!entries.some((entry) => entry.isIntersecting)) {
			return;
		}

		this.prepare();

		if (this.prepared || this.fallback) {
			this.preloadObserver.disconnect();
		}
	}

	destroy() {
		if (!this.video) {
			return;
		}

		this.video.removeEventListener('loadeddata', this.onLoadedData);
		this.video.removeEventListener('error', this.onError);

		this.playObserver.disconnect();
		this.preloadObserver.disconnect();

		if (!this.prepared) {
			return;
		}

		// Het downloaden stoppen als de pagina verlaten wordt: zonder src en
		// zonder sources heeft de video niets meer om te laden
		this.video.pause();
		this.video.querySelectorAll('source').forEach((source) => source.remove());
		this.video.removeAttribute('src');
		this.video.load();
	}
}
